import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordList } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { PrettySlug } from 'noddi-ui-web';
import { formatLicensePlate, getCarDisplayName } from 'noddi-util';
import { NavioTireSeasonChip } from 'noddi-web';

import { getCommonTranslations } from '../../../../utils/commonTranslations';
import { getProgressStep, getQuoteStatusValue, getStepMeta } from '../utils/statusMapping';

type AcceptedRowProps = {
  offer: TireQuoteRecordList;
  onOpen: (offer: TireQuoteRecordList) => void;
};

export const AcceptedRow = ({ offer, onOpen }: AcceptedRowProps) => {
  const status = getQuoteStatusValue(offer);
  const step = getProgressStep(status);
  const meta = getStepMeta(step);
  const quantity = offer.quantitySelected;

  const statusChip = (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
        meta.badge
      )}
    >
      <span className={cn('size-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );

  return (
    <button
      type='button'
      onClick={() => onOpen(offer)}
      className={cn(
        'flex w-full flex-col gap-3 border-b border-border p-4 text-left transition-colors hover:bg-muted',
        'md:grid md:grid-cols-[minmax(180px,260px)_1fr_60px_140px_minmax(80px,1fr)] md:items-center md:gap-4 md:py-3'
      )}
    >
      <div className='flex items-start justify-between gap-3 md:contents'>
        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <div className='truncate text-sm font-semibold text-foreground'>
            {getCarDisplayName({ car: offer.car, showLicensePlate: false, showYear: false })}
          </div>
          <div className='flex items-center gap-1.5 text-[11px] text-muted-foreground'>
            {offer.car.licensePlateNumber && (
              <span className='rounded-sm bg-muted px-1.5 py-0.5 font-mono'>
                {formatLicensePlate(offer.car.licensePlateNumber)}
              </span>
            )}
          </div>
        </div>

        <div className='shrink-0 md:hidden'>{statusChip}</div>
      </div>

      <div className='flex items-center justify-between gap-3 md:contents'>
        <div className='flex min-w-0 items-center'>
          <NavioTireSeasonChip
            season={offer.season.value}
            size='sm'
            translations={getCommonTranslations().seasonBadge}
          />
        </div>

        <div className='text-sm font-semibold text-muted-foreground tabular-nums md:text-center md:text-foreground'>
          <Trans>{quantity} pcs</Trans>
        </div>
      </div>

      <div className='hidden md:flex md:flex-col md:items-start'>{statusChip}</div>

      <div className='flex items-center justify-between gap-2 md:justify-end md:text-right'>
        <div className='text-[11px] text-muted-foreground/80'>
          <PrettySlug slug={offer.slug} />
        </div>
        <NoddiIcon name='ChevronRight' size='small' />
      </div>
    </button>
  );
};
